import { onMounted, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import salesService, { type Sale } from "@/services/sales";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import { useCurrentUser } from "@/composables/useCurrentUser";

interface UseSalesListOptions {
  pos?: number;
  createRoute?: string;
  allowEdit?: boolean;
}

export function useSalesList(options: UseSalesListOptions = {}) {
  const { t } = useI18n();
  const { fetchCurrentUser } = useCurrentUser();
  const salePosValue = options.pos ?? 0;

  const searchQuery = ref("");
  const loading = ref(false);
  const detailsLoading = ref(false);
  const sales = ref<Sale[]>([]);
  const selectedSale = ref<Sale | null>(null);
  const warehouses = ref<Warehouse[]>([]);
  const profileWarehouseId = ref<number | null>(null);

  const filters = reactive({
    warehouse_id: 0,
    status: "",
    payment_status: "",
  });

  const activeFilters = ref({
    warehouse_id: 0,
    status: "",
    payment_status: "",
  });

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchSalesList = async () => {
    loading.value = true;
    try {
      const response = await salesService.getSales({
        q: searchQuery.value,
        warehouse_id: activeFilters.value.warehouse_id || undefined,
        status: activeFilters.value.status || undefined,
        payment_status: activeFilters.value.payment_status || undefined,
        pos: salePosValue,
        per_page: -1,
      });

      sales.value = Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching sales:", error);
      await Swal.fire({
        icon: "error",
        title: t("sales_page.failed_to_load"),
        text: t("sales_page.unable_to_load_sales"),
      });
    } finally {
      loading.value = false;
    }
  };

  const applyFilters = () => {
    activeFilters.value = { ...filters };
    fetchSalesList();
  };

  const resetFilters = () => {
    filters.status = "";
    filters.payment_status = "";
    filters.warehouse_id = profileWarehouseId.value || 0;
    applyFilters();
  };

  const handleViewDetails = async (sale: Sale) => {
    if (!sale?.id) {
      selectedSale.value = sale;
      return;
    }

    detailsLoading.value = true;
    try {
      selectedSale.value = await salesService.getSaleDetails(sale.id);
    } catch (error) {
      console.error("Error fetching sale details:", error);
      selectedSale.value = sale;
      await Swal.fire({
        icon: "error",
        title: t("sales_page.detail_failed"),
        text: t("sales_page.unable_to_load_sale_details"),
      });
    } finally {
      detailsLoading.value = false;
    }
  };

  const refreshSelectedSale = async () => {
    if (!selectedSale.value?.id) return;
    selectedSale.value = await salesService.getSaleDetails(selectedSale.value.id);
  };

  const handlePaymentUpdated = async () => {
    try {
      await refreshSelectedSale();
      await fetchSalesList();
    } catch (error) {
      console.error("Error refreshing sale after payment update:", error);
    }
  };

  const handleDeleteSale = async (sale: Sale) => {
    if (!sale.id) return;

    const result = await Swal.fire({
      title: t("sales_page.delete_sale_title"),
      text: t("sales_page.delete_sale_text", { ref: sale.reference_no }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("sales_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await salesService.deleteSale(sale.id);
      await fetchSalesList();
      await Swal.fire({
        icon: "success",
        title: t("sales_page.deleted"),
        text: t("sales_page.sale_deleted_successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting sale:", error);
      await Swal.fire({
        icon: "error",
        title: t("sales_page.delete_failed"),
        text: t("sales_page.unable_to_delete_sale"),
      });
    }
  };

  watch(searchQuery, fetchSalesList);

  onMounted(async () => {
    await fetchWarehouses();

    const currentUser = await fetchCurrentUser(true);
    if (currentUser?.warehouse_id) {
      profileWarehouseId.value = Number(currentUser.warehouse_id);
      filters.warehouse_id = Number(currentUser.warehouse_id);
      activeFilters.value.warehouse_id = Number(currentUser.warehouse_id);
    }

    await fetchSalesList();
  });

  return {
    searchQuery,
    loading,
    detailsLoading,
    sales,
    selectedSale,
    warehouses,
    filters,
    profileWarehouseId,
    applyFilters,
    resetFilters,
    handleViewDetails,
    handlePaymentUpdated,
    handleDeleteSale,
    createRoute: options.createRoute ?? "/create-sales",
    allowEdit: options.allowEdit ?? true,
  };
}
